import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

/**
 * Tells the server which page is open and for how long, so the Super Admin can see how many people visit and where they spend time
 * (routes/siteVisits.js; what is counted and kept is in services/siteVisits.js).
 *
 * Nothing is stored in the browser: no cookie, no id that outlives the page. Each page view makes a random id of its own, reports when it
 * opens, and reports again when the visitor moves on, switches away or leaves, with the seconds it was in front. Time with the tab in the
 * background is not counted. A visitor whose browser sends Global Privacy Control or Do Not Track is not reported at all, and neither is
 * a page whose address carries a secret (an offer, an interview time, a password reset): the address is not sent, so it cannot be kept.
 *
 * It renders nothing and must never get in the way: every call is allowed to fail and the page goes on.
 */

const ENDPOINT = '/api/public/visit';

/** Addresses that carry a secret in the path. The server refuses them too; this keeps them from being sent at all. */
const SECRET = /^\/(offer|schedule|interviewer|interviewing|group-invite|reset-password|activate|confirm-email)(\/|$)|^\/interview\/|^\/signup\/complete/;

/** Whether this browser has asked not to be measured. */
export const optedOut = (nav = typeof navigator === 'undefined' ? {} : navigator) => nav.globalPrivacyControl === true || nav.doNotTrack === '1';

/** Whether a page is worth reporting: not a secret link, not the developer's preview. */
export const reportable = (pathname) => !SECRET.test(pathname) && !pathname.startsWith('/dev/');

/** The page as the server counts it: the address, and for a tabbed page which tab. */
export const pageOf = (pathname, search = '') => {
  const tab = new URLSearchParams(search).get('tab');
  return tab ? `${pathname}?tab=${encodeURIComponent(tab)}` : pathname;
};

/** A random id for one page view; it identifies nobody and is not kept. */
export const newViewId = () => {
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) crypto.getRandomValues(bytes);
  else bytes.forEach((_, index) => { bytes[index] = Math.floor(Math.random() * 256); });
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
};

const send = (body) => {
  try {
    const data = JSON.stringify(body);
    if (typeof navigator !== 'undefined' && navigator.sendBeacon && navigator.sendBeacon(ENDPOINT, new Blob([data], { type: 'application/json' }))) return;
    fetch(ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: data, keepalive: true }).catch(() => {});
  } catch {
    // Counting is never worth breaking a page for.
  }
};

/** Where the visitor came from, which only the page they arrived on can say. */
const arrival = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    entry: true,
    ref: document.referrer || undefined,
    src: params.get('src') || undefined,
    utm: { source: params.get('utm_source') || undefined, medium: params.get('utm_medium') || undefined, campaign: params.get('utm_campaign') || undefined },
  };
};

const VisitTracker = () => {
  const { pathname, search } = useLocation();
  const page = pageOf(pathname, search);
  const first = useRef(true);

  useEffect(() => {
    const entry = first.current;
    first.current = false;
    if (optedOut() || !reportable(pathname)) return undefined;

    const view = { vid: newViewId(), path: page, shownSince: document.visibilityState === 'hidden' ? null : Date.now(), ms: 0 };
    const seconds = () => Math.round((view.ms + (view.shownSince ? Date.now() - view.shownSince : 0)) / 1000);
    const report = (extra = {}) => send({ vid: view.vid, path: view.path, seconds: seconds(), ...extra });

    report(entry ? arrival() : {});

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        if (view.shownSince) { view.ms += Date.now() - view.shownSince; view.shownSince = null; }
        report();
      } else if (!view.shownSince) {
        view.shownSince = Date.now();
      }
    };
    const onLeave = () => report();

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onLeave);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onLeave);
      // Moving to another page: say how long this one was open.
      report();
    };
    // `pathname` is covered by `page`; the rest of the query (filters, a chosen job) is not a different page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  return null;
};

export default VisitTracker;
